(function(global, factory) {
  typeof exports === "object" && typeof module !== "undefined" ? factory(exports, require("zod")) : typeof define === "function" && define.amd ? define(["exports", "zod"], factory) : (global = typeof globalThis !== "undefined" ? globalThis : global || self, factory(global.TTAGameData = {}, global.Zod));
})(this, function(exports2, zod) {
  "use strict";
  function requiredArgs(required, args) {
    if (args.length < required) {
      throw new TypeError(required + " argument required, but only " + args.length + " present");
    }
  }
  function _typeof$1(obj) {
    "@babel/helpers - typeof";
    if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
      _typeof$1 = function _typeof2(obj2) {
        return typeof obj2;
      };
    } else {
      _typeof$1 = function _typeof2(obj2) {
        return obj2 && typeof Symbol === "function" && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
      };
    }
    return _typeof$1(obj);
  }
  function isDate(value) {
    requiredArgs(1, arguments);
    return value instanceof Date || _typeof$1(value) === "object" && Object.prototype.toString.call(value) === "[object Date]";
  }
  function _typeof(obj) {
    "@babel/helpers - typeof";
    if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
      _typeof = function _typeof2(obj2) {
        return typeof obj2;
      };
    } else {
      _typeof = function _typeof2(obj2) {
        return obj2 && typeof Symbol === "function" && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
      };
    }
    return _typeof(obj);
  }
  function toDate(argument) {
    requiredArgs(1, arguments);
    var argStr = Object.prototype.toString.call(argument);
    if (argument instanceof Date || _typeof(argument) === "object" && argStr === "[object Date]") {
      return new Date(argument.getTime());
    } else if (typeof argument === "number" || argStr === "[object Number]") {
      return new Date(argument);
    } else {
      if ((typeof argument === "string" || argStr === "[object String]") && typeof console !== "undefined") {
        console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#string-arguments");
        console.warn(new Error().stack);
      }
      return /* @__PURE__ */ new Date(NaN);
    }
  }
  function isValid(dirtyDate) {
    requiredArgs(1, arguments);
    if (!isDate(dirtyDate) && typeof dirtyDate !== "number") {
      return false;
    }
    var date = toDate(dirtyDate);
    return !isNaN(Number(date));
  }
  function parseJSON(argument) {
    requiredArgs(1, arguments);
    if (typeof argument === "string") {
      var parts = argument.match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d{0,7}))?(?:Z|(.)(\d{2}):?(\d{2})?)?/);
      if (parts) {
        return new Date(Date.UTC(+parts[1], +parts[2] - 1, +parts[3], +parts[4] - (+parts[9] || 0) * (parts[8] == "-" ? -1 : 1), +parts[5] - (+parts[10] || 0) * (parts[8] == "-" ? -1 : 1), +parts[6], +((parts[7] || "0") + "00").substring(0, 3)));
      }
      return /* @__PURE__ */ new Date(NaN);
    }
    return toDate(argument);
  }
  const ZTodo = zod.z.unknown();
  const ZUnused = zod.z.unknown().nullish();
  const ZInt = zod.z.number().int();
  const ZUInt = ZInt.nonnegative();
  const ZDatabaseId = ZUInt;
  const ZDateString = zod.z.string().superRefine((date, ctx) => {
    if (!isValid(parseJSON(date))) {
      ctx.addIssue({
        code: zod.z.ZodIssueCode.invalid_date,
        message: "Invalid Date -- Could not be parsed by date-fns parseJSON()"
      });
    }
  });
  const permissiveUuidRegExp = new RegExp(
    [
      // UUID Versions 1-5
      "^([a-f0-9]{8}-?[a-f0-9]{4}-?[1-5][a-f0-9]{3}-?[a-f0-9]{4}-?[a-f0-9]{8,12})$",
      // Nil UUID
      "^(0{8}-?0{4}-?0{4}-?0{4}-?0{8,12})$"
    ].join("|"),
    "i"
  );
  const ZUuid = zod.z.string().superRefine((uuid, ctx) => {
    if (!permissiveUuidRegExp.test(uuid)) {
      ctx.addIssue({
        code: zod.z.ZodIssueCode.invalid_string,
        validation: "uuid",
        message: "Invalid UUID"
      });
    }
  });
  const GameType = zod.z.enum([
    "jump",
    "match",
    "scramble",
    "recall",
    "maze",
    "grid",
    "trivia",
    "scenarios",
    "jeopardy",
    "5s",
    "wbs",
    "detective",
    "flash",
    "scorm",
    "wof",
    "sortit",
    "xapi",
    "noodle"
  ]);
  const GameMode = zod.z.enum(["standard", "instructor_lead", "instructor_initiated"]);
  const AuthorizationType = zod.z.nativeEnum({
    none: 0,
    user_list: 1,
    sso: 2
  });
  const QuestionType = zod.z.enum([
    "multiple_choice",
    "multiple_select",
    "slider",
    "image",
    "scramble_letter",
    "scramble_sentence",
    "text_input",
    "grid",
    "interstitial",
    "poll",
    "detective",
    "wof_regular",
    "wof_tossup",
    "wof_bonus",
    "noodle_puzzle"
  ]);
  const AnswerType = zod.z.enum(["text", "image", "empty", "default"]);
  const MatchType = zod.z.enum(["exact", "contains", "starts_with", "ends_with", "inside"]);
  function zPartialRecord(keySchema, valueType, params) {
    return zod.z.record(keySchema, valueType, params);
  }
  const zTranslations = (valueType) => {
    return zod.z.union([zPartialRecord(valueType), zod.z.array(zod.z.never()).length(0)]).transform((value) => Array.isArray(value) ? {} : value);
  };
  const MediaDefinition = zod.z.object({
    url: zod.z.string().url().nullish()
  });
  const ImageDefinition = MediaDefinition;
  const AudioDefinition = MediaDefinition;
  const VideoDefinition = MediaDefinition.extend({
    type: zod.z.literal("external").nullish()
  });
  const ImageWithResizedDefinition = zod.z.object({
    original: zod.z.string().url().nullish(),
    resized: zod.z.string().url().nullish(),
    svg: zod.z.string().url().nullish()
  });
  const TranslatableAnswerFields = zod.z.object({
    answer_id: ZDatabaseId,
    image: ImageWithResizedDefinition,
    image_logo: ZUnused,
    // Shouldn't exist.
    video: ZUnused,
    // Shouldn't exist.
    text: zod.z.string().nullish(),
    body: zod.z.string().nullish(),
    // Only used in Detective
    type: AnswerType.nullish(),
    match_type: MatchType.nullish(),
    response: ZUnused
    // Shouldn't exist.
  });
  const TranslatableAnswerFieldsInAnswer = TranslatableAnswerFields.omit({
    // These keys appear in answer translations but not the base answer.
    image_logo: true,
    video: true
  });
  const AnswerBase = zod.z.object({
    question_list_answer_id: ZDatabaseId.nullish(),
    points: ZInt.nullish(),
    correct: zod.z.boolean(),
    require: zod.z.boolean(),
    row: ZUInt.nullish(),
    col: ZUInt.nullish(),
    next_question_id: ZDatabaseId.nullish().or(zod.z.literal(-1)),
    next_question_hash: ZUuid.nullish().or(zod.z.literal("-1"))
  });
  const AnswerBaseTranslated = AnswerBase.merge(TranslatableAnswerFieldsInAnswer).extend({
    translations: zTranslations(TranslatableAnswerFields)
  });
  const Answer = zod.z.lazy(
    () => AnswerBaseTranslated.extend({
      answers: zod.z.array(AnswerBaseTranslated).nullish()
      // Only used in Detective
    })
  );
  const Base = { ZDatabaseId, ZDateString, ZInt, ZTodo, ZUInt, ZUnused, ZUuid };
  var Enums;
  ((Enums2) => {
    Enums2.AnswerType = AnswerType;
    Enums2.AuthorizationType = AuthorizationType;
    Enums2.GameMode = GameMode;
    Enums2.GameType = GameType;
    Enums2.MatchType = MatchType;
    Enums2.QuestionType = QuestionType;
  })(Enums || (Enums = {}));
  const TranslatableAdvancedFeedbackFields = zod.z.object({
    advanced_feedback_id: ZDatabaseId,
    image: ImageWithResizedDefinition,
    audio: AudioDefinition,
    video: VideoDefinition,
    text: zod.z.string().nullish()
  });
  const AdvancedFeedback = TranslatableAdvancedFeedbackFields.extend({
    layout: zod.z.string().nullish(),
    bg_color: zod.z.string().nullish(),
    watch_video: zod.z.boolean(),
    translations: zTranslations(TranslatableAdvancedFeedbackFields)
  });
  const TranslatableQuestionFields = zod.z.object({
    question_id: ZDatabaseId,
    image: ImageWithResizedDefinition,
    placeholder: ImageWithResizedDefinition,
    audio: AudioDefinition,
    video: VideoDefinition,
    text: zod.z.string().nullish(),
    body: zod.z.string().nullish(),
    answer_phrase: zod.z.string().nullish(),
    intro_title: zod.z.string().nullish(),
    intro_top: zod.z.string().nullish(),
    intro_top_2: zod.z.string().nullish(),
    intro_body: zod.z.string().nullish(),
    intro_button: zod.z.string().nullish(),
    results_title: zod.z.string().nullish(),
    results_top: zod.z.string().nullish(),
    results_body: zod.z.string().nullish(),
    results_button: zod.z.string().nullish(),
    prestige_title: zod.z.string().nullish(),
    prestige_top: zod.z.string().nullish(),
    prestige_body: zod.z.string().nullish(),
    prestige_button: zod.z.string().nullish(),
    info_1: zod.z.string().nullish(),
    info_2: zod.z.string().nullish(),
    response_correct: zod.z.string().nullish(),
    response_correct_image: ImageWithResizedDefinition,
    response_incorrect: zod.z.string().nullish(),
    response_incorrect_image: ImageWithResizedDefinition,
    response_timeout: zod.z.string().nullish(),
    response_timeout_image: ImageWithResizedDefinition,
    row_titles: zod.z.array(
      zod.z.object({
        question_grid_id: ZDatabaseId.nullish(),
        language_id: ZDatabaseId.nullish(),
        text: zod.z.string()
      })
    ),
    column_titles: zod.z.array(
      zod.z.object({
        question_grid_id: ZDatabaseId.nullish(),
        language_id: ZDatabaseId.nullish(),
        text: zod.z.string()
      })
    )
  });
  const TranslatableQuestionFieldsInQuestion = zod.z.object(TranslatableQuestionFields.shape);
  const QuestionBase$1 = zod.z.object({
    question_type: QuestionType,
    randomize_answers: zod.z.boolean(),
    time: ZUInt,
    points_correct: ZInt.nullish(),
    points_incorrect: ZInt.nullish(),
    answers: zod.z.array(Answer),
    hash: ZUuid.nullish(),
    advanced_feedback: zod.z.array(AdvancedFeedback),
    skip_clue: zod.z.boolean()
  });
  const GroupQuestions = zod.z.object({
    question_group_id: ZDatabaseId.nullish(),
    question_list_question_id: ZDatabaseId.nullish()
  });
  const InterstitialQuestions = zod.z.object({
    watch_video: zod.z.boolean().nullish(),
    // Only used in `interstitial` type questions.
    interstitial: zod.z.object({
      text_color: zod.z.string().nullish(),
      // Hex Color (#00000000)
      text_shadow: zod.z.boolean().nullish(),
      bg_color: zod.z.string().nullish(),
      // Hex Color (#00000000)
      name: zod.z.string().nullish()
    })
    // Only used in `interstitial` type questions.
  });
  const GridQuestions = zod.z.object({
    total_rows: ZUInt,
    // Only used in `grid` type questions.
    total_cols: ZUInt
    // Only used in `grid` type questions.
  });
  const PollQuestion = zod.z.object({
    show_results: zod.z.boolean().nullish()
    // Only used in `poll` type questions.
  });
  const QuestionBaseTranslated = zod.z.object({
    ...GroupQuestions.shape,
    ...QuestionBase$1.shape,
    ...TranslatableQuestionFieldsInQuestion.shape,
    ...InterstitialQuestions.shape,
    ...GridQuestions.shape,
    ...PollQuestion.shape,
    translations: zTranslations(TranslatableQuestionFields)
  });
  const Question$1 = QuestionBaseTranslated;
  const RegistrationField = zod.z.object({
    key: zod.z.string(),
    label: zod.z.string(),
    condition: zod.z.enum(["required", "optional", "disabled"]).nullish(),
    show_in_leaderboard: zod.z.boolean()
  });
  const CustomRegistrationField = RegistrationField.extend({
    custom_field_id: ZDatabaseId,
    domain_id: ZDatabaseId,
    type: zod.z.enum(["text", "dropdown"]),
    name: zod.z.string(),
    date_created: ZDateString,
    game_id: ZDatabaseId,
    sort_order: ZUInt,
    language_id: ZDatabaseId,
    options: zod.z.array(
      zod.z.object({
        custom_field_option_id: ZDatabaseId,
        custom_field_id: ZDatabaseId,
        value: ZUnused,
        // Not used
        sort_order: ZUInt,
        language_id: ZDatabaseId,
        name: zod.z.string()
      })
    )
  });
  const UserList = zod.z.object({
    user_list_id: ZDatabaseId,
    player_list_id: ZDatabaseId,
    name: zod.z.string()
  });
  const AvailableTranslation = zod.z.object({
    language_id: ZDatabaseId,
    name: zod.z.string(),
    code: zod.z.string()
  });
  const AssetsDefinition = zod.z.object({
    background: zod.z.string().nullish(),
    character: zod.z.string().nullish(),
    theme_custom_color_1: zod.z.string().nullish(),
    theme_custom_color_2: zod.z.string().nullish(),
    theme_custom_color_3: zod.z.string().nullish(),
    theme_custom_color_4: zod.z.string().nullish(),
    theme_custom_color_5: zod.z.string().nullish(),
    theme_custom_color_6: zod.z.string().nullish(),
    theme_custom_invert_text: zod.z.boolean(),
    track: zod.z.string().url().nullish(),
    theme: zod.z.string(),
    social: zod.z.boolean()
  });
  const PopupDefinition = zod.z.object({
    title: zod.z.string().nullish(),
    body: zod.z.string().nullish()
  });
  const SplashDefinition = zod.z.object({
    logo: zod.z.object({
      type: zod.z.enum(["text", "image"]).nullish(),
      text: zod.z.string().nullish(),
      image: ImageDefinition
    }),
    title: zod.z.object({
      type: zod.z.enum(["default", "text", "image", "none"]).nullish(),
      text: zod.z.string().nullish(),
      image: ImageDefinition
    }),
    background: zod.z.object({
      type: zod.z.enum(["image"]).nullish(),
      image: ImageDefinition
    })
  });
  const TranslatableGameFields$1 = zod.z.object({
    popup: PopupDefinition,
    splash: SplashDefinition.omit({
      background: true
      // In `translations[]`, there is no `background` key.
    }),
    learn_more_button_text: zod.z.string().nullish()
  });
  const TranslatableGameFieldsInGame$1 = TranslatableGameFields$1.extend({
    splash: SplashDefinition
  });
  const SSO = zod.z.object({
    sso_provider_id: ZDatabaseId.nullish(),
    sso_redirect_url: zod.z.string().url().nullish().or(zod.z.literal("")),
    sso_timeout: ZUInt.nullish()
  });
  const Unused = zod.z.object({
    randomize_answers: zod.z.boolean(),
    // No longer used
    privacy: ZUnused,
    // (Boolean) No longer used
    token: ZUnused
    // (z.string()) No longer used
  });
  const PrivacyPolicy = zod.z.object({
    privacy: zod.z.boolean().nullish(),
    privacy_policy_url: zod.z.string().url().nullish().or(zod.z.literal("")),
    privacy_policy_name: zod.z.string().nullish(),
    privacy_policy_required: zod.z.boolean()
  });
  const XAPI = zod.z.object({
    xapi_endpoint: zod.z.string().url().nullish(),
    xapi_auth: zod.z.string().nullish(),
    xapi_activity_id: zod.z.string().nullish()
  });
  const GameBase$1 = zod.z.object({
    game_id: ZDatabaseId,
    name: zod.z.string().min(1),
    slug: zod.z.string().min(1),
    expired: zod.z.boolean(),
    game_mode: GameMode,
    game_type: GameType,
    tutorial: zod.z.boolean(),
    // Used in everything except Jeopardy
    authorization_type: AuthorizationType,
    user_lists: zod.z.array(UserList),
    learn_more_url: zod.z.string().url().nullish().or(zod.z.literal("")),
    language_id: ZDatabaseId,
    available_translations: zod.z.array(AvailableTranslation),
    randomize_questions: zod.z.boolean(),
    // Only used in Jump, Match, Recall, Scramble, Trivia, Noodle
    time_per_question: ZUInt,
    allow_no_answer: zod.z.boolean(),
    assets: AssetsDefinition,
    date_created: ZDateString,
    created_by: ZDatabaseId.nullish(),
    questions: zod.z.array(Question$1),
    // Used in games that don't use Question Groups
    divisions: zod.z.array(ZTodo).nullish(),
    // TODO: What does this mean?
    registration: zod.z.object({
      required: zod.z.boolean(),
      fields: zod.z.array(RegistrationField),
      custom_fields: zod.z.array(CustomRegistrationField)
    }),
    show_leaderboard: zod.z.boolean(),
    percent_required: ZUInt.lte(100),
    game_over: SplashDefinition.pick({ background: true }),
    tweet_button: zod.z.object({
      show: zod.z.boolean(),
      text: zod.z.string().nullish()
    }),
    // Only used in Recall, Jump, Match, Trivia, Scenarios, Scramble, WBS
    meta: ZTodo,
    // Only used in WBS (TODO: Possible utility field?)
    scorm_close_button: zod.z.boolean(),
    scorm_pass_fail: zod.z.boolean(),
    settings: zod.z.object({}),
    // Used in J for Daily Doubles. TODO: Used for anything else?
    is_archived: zod.z.boolean(),
    is_deleted: zod.z.boolean(),
    url: zod.z.string().url(),
    icon: zod.z.string().url().nullish()
  });
  const GameBaseTranslated$1 = zod.z.object({
    ...GameBase$1.shape,
    ...SSO.shape,
    ...XAPI.shape,
    ...Unused.shape,
    ...PrivacyPolicy.shape,
    ...TranslatableGameFieldsInGame$1.shape,
    translations: zTranslations(TranslatableGameFields$1)
  });
  const Game$1 = GameBaseTranslated$1;
  const GameStateBase = zod.z.object({
    language_id: ZDatabaseId,
    mode: zod.z.enum(["virtual", "live"]),
    // Virtual vs In-Person VILT mode
    question_id: ZDatabaseId,
    // Used by CMS
    advanced_feedback_index: ZInt,
    // Used by CMS instructor view, Jeopardy, and Trivia
    instructor_username: zod.z.string(),
    instructor_id: ZDatabaseId,
    session_group_id: ZDatabaseId,
    slug: zod.z.string(),
    name: zod.z.string().nullish()
    // Used by CMS instructor view (maybe?)
  });
  const StateFlags = zod.z.object({
    game_initiate: zod.z.boolean().nullish(),
    game_start: zod.z.boolean().nullish(),
    game_end: zod.z.boolean().nullish(),
    game_mute: zod.z.boolean().nullish(),
    show_stats: zod.z.boolean().nullish(),
    show_poll: zod.z.boolean().nullish(),
    show_clue: zod.z.boolean().nullish(),
    show_answers: zod.z.boolean().nullish(),
    show_correct: zod.z.boolean().nullish(),
    show_leaderboard: zod.z.boolean().nullish()
  });
  const GameState = zod.z.object({
    ...GameStateBase.shape,
    ...StateFlags.shape
  });
  const TranslatableQuestionGroupFields = zod.z.object({
    question_group_id: ZDatabaseId,
    image: ImageWithResizedDefinition,
    audio: AudioDefinition,
    video: VideoDefinition,
    text: zod.z.string().nullish()
  });
  const TranslatableQuestionGroupFieldsInQuestionGroup = zod.z.object(TranslatableQuestionGroupFields.shape);
  const QuestionGroupBase = zod.z.object({
    parent_question_group_id: ZDatabaseId.nullish(),
    game_id: ZDatabaseId,
    name: zod.z.string().nullish(),
    randomize_questions: zod.z.boolean(),
    // Only used in Recall
    sort_order: ZUInt,
    date_created: ZDateString,
    questions: zod.z.array(Question$1),
    time: ZUInt,
    // Only used in Recall
    num_categories: ZUInt.nullish(),
    // Only used in Jeopardy CMS
    num_questions: ZUInt.nullish()
    // Only used in Jeopardy CMS
  });
  const QuestionGroupBaseTranslated = QuestionGroupBase.merge(
    TranslatableQuestionGroupFieldsInQuestionGroup
  ).extend({
    translations: zTranslations(TranslatableQuestionGroupFields)
  });
  const QuestionGroup = zod.z.lazy(
    () => QuestionGroupBaseTranslated.extend({
      question_groups: zod.z.array(QuestionGroup).nullish()
    })
  );
  const index$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
    __proto__: null,
    AdvancedFeedback,
    Answer,
    AnswerBase,
    AnswerType,
    AudioDefinition,
    AuthorizationType,
    Base,
    get Enums() {
      return Enums;
    },
    Game: Game$1,
    GameBase: GameBase$1,
    GameMode,
    GameState,
    GameType,
    ImageDefinition,
    ImageWithResizedDefinition,
    InterstitialQuestions,
    MatchType,
    PollQuestion,
    PrivacyPolicy,
    Question: Question$1,
    QuestionBase: QuestionBase$1,
    QuestionGroup,
    QuestionType,
    SSO,
    TranslatableAdvancedFeedbackFields,
    TranslatableAnswerFields,
    TranslatableGameFields: TranslatableGameFields$1,
    TranslatableGameFieldsInGame: TranslatableGameFieldsInGame$1,
    TranslatableQuestionFields,
    VideoDefinition,
    XAPI,
    ZDatabaseId,
    ZDateString,
    ZInt,
    ZTodo,
    ZUInt,
    ZUnused,
    ZUuid,
    zTranslations
  }, Symbol.toStringTag, { value: "Module" }));
  const QuestionBase = Question$1.extend({
    layout: zod.z.string().nullish(),
    // Only used in Scenarios, WBS
    next_question_id_correct: ZDatabaseId.nullish().or(zod.z.literal(-1)),
    // Only used in Scenarios
    next_question_id_incorrect: ZDatabaseId.nullish().or(zod.z.literal(-1)),
    // Only used in Scenarios
    next_question_id_timeout: ZDatabaseId.nullish().or(zod.z.literal(-1)),
    // Only used in Scenarios
    next_question_hash_correct: ZUuid.nullish().or(zod.z.literal("-1")),
    // Only used in Scenarios
    next_question_hash_incorrect: ZUuid.nullish().or(zod.z.literal("-1")),
    // Only used in Scenarios
    next_question_hash_timeout: ZUuid.nullish().or(zod.z.literal("-1"))
    // Only used in Scenarios
  });
  const Question = QuestionBase;
  const TranslatableGameFields = zod.z.object({
    ...TranslatableGameFields$1.shape,
    meter_title_1: zod.z.string().nullish(),
    // Only used in Scenarios
    meter_title_2: zod.z.string().nullish()
    // Only used in Scenarios
  });
  const TranslatableGameFieldsInGame = zod.z.object({
    ...TranslatableGameFieldsInGame$1.shape,
    meter_title_1: zod.z.string().nullish(),
    // Only used in Scenarios
    meter_title_2: zod.z.string().nullish()
    // Only used in Scenarios
  });
  const GameBase = zod.z.object({
    ...Game$1.omit({
      questions: true
    }).shape,
    metering: zod.z.boolean(),
    // Only used in Scenarios
    meter_type: ZTodo,
    // Only used in Scenarios
    question_transition: zod.z.boolean(),
    // Only used in Scenarios
    repeat_question_history: zod.z.boolean(),
    // Only used in Scenarios
    enable_answer_highlight: zod.z.boolean(),
    // Only used in Scenarios
    enable_question_history: zod.z.boolean(),
    // Only used in Scenarios
    questions: zod.z.array(Question)
  });
  const GameBaseTranslated = zod.z.object({
    ...GameBase.shape,
    ...TranslatableGameFieldsInGame.shape,
    translations: zTranslations(TranslatableGameFields)
  });
  const Game = GameBaseTranslated;
  const index = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
    __proto__: null,
    Answer,
    Base,
    get Enums() {
      return Enums;
    },
    Game,
    GameBase: GameBase$1,
    Question
  }, Symbol.toStringTag, { value: "Module" }));
  exports2.General = index$1;
  exports2.Scenarios = index;
  Object.defineProperty(exports2, Symbol.toStringTag, { value: "Module" });
});

